import { RETRACED_COMPANY_ID } from '../../../companies/constants.js';
import {
  actorFromAuditMeta,
  type DocumentTypeHistoryMetadata,
  diffRecords,
  ENTITY_HISTORY_ENTITY_TYPES,
  ENTITY_HISTORY_EVENT_TYPES,
  ENTITY_HISTORY_SOURCES,
  type EntityHistoryService,
  type ValueRecord,
} from '../../../entity-history/index.js';

/** The type's recorded fields, by their API names. */
export const DOCUMENT_TYPE_HISTORY_FIELDS = [
  'title',
  'description',
  'shortDescription',
  'documentClass',
  'impactAreaId',
  'isDeprecated',
] as const;

export type DocumentTypeHistoryValues = Record<
  (typeof DOCUMENT_TYPE_HISTORY_FIELDS)[number],
  unknown
>;

export interface DocumentTypeHistoryContext {
  /** The actor: `ctx.userMeta`. */
  audit: object;
  /** The request id, to group the entries of one request. */
  correlationId?: string | null;
}

const METADATA: DocumentTypeHistoryMetadata = {
  entityType: ENTITY_HISTORY_ENTITY_TYPES.DOCUMENT_TYPE,
};

const asValueRecord = (
  values: DocumentTypeHistoryValues | null,
): ValueRecord | undefined =>
  values
    ? Object.fromEntries(
        Object.entries(values).map(([field, value]) => [field, { value }]),
      )
    : undefined;

/**
 * A document type was created (`from` is null: its fields as `from: null`
 * changes), edited, deprecated or restored (the fields that moved), or
 * deleted (`to` is null: its last values as `to: null` changes, so a deleted
 * type stays recoverable). A save that moved nothing is not recorded. The
 * catalogue is platform-wide and only staff write it, so the entry belongs to
 * Retraced's own company.
 */
export const recordDocumentTypeChange = async (
  history: EntityHistoryService,
  documentTypeId: string,
  values: {
    from: DocumentTypeHistoryValues | null;
    to: DocumentTypeHistoryValues | null;
  },
  context: DocumentTypeHistoryContext,
): Promise<void> => {
  const changes = diffRecords(
    asValueRecord(values.from),
    asValueRecord(values.to),
    { fields: DOCUMENT_TYPE_HISTORY_FIELDS },
  );
  const eventType =
    values.from === null
      ? ENTITY_HISTORY_EVENT_TYPES.CREATED
      : values.to === null
        ? ENTITY_HISTORY_EVENT_TYPES.DELETED
        : ENTITY_HISTORY_EVENT_TYPES.UPDATED;
  if (
    eventType === ENTITY_HISTORY_EVENT_TYPES.UPDATED &&
    changes.length === 0
  ) {
    return;
  }

  await history.record({
    entityType: ENTITY_HISTORY_ENTITY_TYPES.DOCUMENT_TYPE,
    entityId: documentTypeId,
    eventType,
    source: ENTITY_HISTORY_SOURCES.ADMIN,
    correlationId: context.correlationId ?? null,
    companyIds: {
      forCompanyId: RETRACED_COMPANY_ID,
      byCompanyId: RETRACED_COMPANY_ID,
      onBehalfOfCompanyId: RETRACED_COMPANY_ID,
    },
    changes,
    metadata: METADATA,
    createdBy: actorFromAuditMeta(context.audit as Record<string, unknown>),
  });
};
